/**
 * PlayersPortalModal — full Players Portal UI in a single modal.
 *
 * Views (driven by `view` state):
 *   - "list"   : search bar + list of players + "Add new" button.
 *                Pasting a unique code or phone into the search bar performs
 *                a cross-club lookup — even if the player isn't in this
 *                cafe's list, the modal will surface them (and their
 *                blacklist status) so the admin can decide.
 *   - "add"    : Add-player form. Webcam capture for photo, fields, etc.
 *                Phone is debounce-checked for duplicates → if a match is
 *                found, the existing player is shown instead.
 *   - "detail" : Profile + tabs (History / Damages / Blacklist).
 *
 * RBAC reflected in the UI:
 *   - admin   : can add players + record damages. Cannot blacklist / delete.
 *   - owner   : everything admin can + blacklist/release. Cannot delete /
 *               edit phone.
 *   - platform: everything + delete + edit phone.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  X, Search, UserPlus, ArrowLeft, Camera, RefreshCw, Copy, QrCode,
  ShieldAlert, ShieldCheck, Trash2, Phone, Calendar, MapPin, Hash, History,
  AlertTriangle, RotateCcw, Fingerprint as FingerprintIcon, CheckCircle2,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { captureFingerprint, forgetMantraService, matchTemplates } from "@/lib/mantra";

const GENDERS = ["male", "female", "other"];

function Spinner() {
  return (
    <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-emerald-400 border-t-transparent" />
  );
}

function formatINR(amount) {
  if (amount == null) return "—";
  return `₹${Number(amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

/* ------------------------------------------------------------------ */
/*                          Add-player form                            */
/* ------------------------------------------------------------------ */

function AddPlayerForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    full_name: "", phone: "", dob: "", gender: "",
    city: "", state: "", country: "India",
  });
  const [photoB64, setPhotoB64] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [phoneCheckMsg, setPhoneCheckMsg] = useState("");
  const [existingMatch, setExistingMatch] = useState(null);
  // Mantra MFS110 fingerprint capture state
  const [fingerprint, setFingerprint] = useState(null); // { template, quality }
  const [fpBusy, setFpBusy] = useState(false);

  // Webcam
  const videoRef = useRef(null);
  const [streamOn, setStreamOn] = useState(false);
  const [stream, setStream] = useState(null);

  const stopCamera = useCallback(() => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
    setStreamOn(false);
  }, [stream]);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 480, height: 480 },
        audio: false,
      });
      setStream(s);
      setStreamOn(true);
      // Attach in the next tick once the <video> element exists.
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
      }, 30);
    } catch (e) {
      toast.error("Camera access denied or unavailable.");
    }
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement("canvas");
    const size = Math.min(v.videoWidth, v.videoHeight, 480);
    c.width = size; c.height = size;
    const ctx = c.getContext("2d");
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
    setPhotoB64(c.toDataURL("image/jpeg", 0.7));
    stopCamera();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setPhotoB64(String(r.result || ""));
    r.readAsDataURL(f);
  };

  // Live phone duplicate check (debounced)
  useEffect(() => {
    const phone = form.phone.trim();
    if (phone.length < 7) { setPhoneCheckMsg(""); setExistingMatch(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/portal/players/lookup", { params: { phone } });
        setExistingMatch(data);
        setPhoneCheckMsg("");
      } catch (e) {
        if (e.response?.status === 404) {
          setExistingMatch(null);
          setPhoneCheckMsg("New phone — will register.");
        }
      }
    }, 500);
    return () => clearTimeout(t);
  }, [form.phone]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // -------------------- Mantra MFS110 fingerprint scan --------------------
  const scanFingerprint = async () => {
    setFpBusy(true);
    try {
      const result = await captureFingerprint({ timeoutMs: 10000 });
      // Biometric 1:N duplicate check via Mantra's local matcher. If a
      // duplicate is found we hard-reject the scan — the same finger can
      // only belong to one membership. The backend also enforces this via
      // an exact-hash uniqueness check as a safety net.
      let index = [];
      try {
        const { data } = await api.get("/portal/players/fingerprint-index");
        index = Array.isArray(data) ? data : [];
      } catch {
        // Non-fatal — backend hash check will still catch trivial dupes.
      }
      let matchedPlayer = null;
      for (const p of index) {
        if (!p.fingerprint_template) continue;
        const { matched, unsupported } = await matchTemplates(
          result.template,
          p.fingerprint_template,
        );
        if (unsupported) break; // matcher not available → skip 1:N loop
        if (matched) { matchedPlayer = p; break; }
      }
      if (matchedPlayer) {
        toast.error(
          `This fingerprint is already registered to ${matchedPlayer.full_name} (${matchedPlayer.unique_code}). Each fingerprint can only belong to one membership.`,
        );
        return;
      }
      setFingerprint(result);
      toast.success(
        `Fingerprint captured (quality: ${result.quality || "n/a"}).`,
      );
    } catch (err) {
      forgetMantraService(); // force rediscovery next attempt
      toast.error(
        err?.message ||
          "Couldn't reach the Mantra MFS110 sensor. Make sure it's plugged in and the Mantra Client Service is running.",
      );
    } finally {
      setFpBusy(false);
    }
  };

  const clearFingerprint = () => setFingerprint(null);

  // ---------------------------------------------------------------
  // Registration gate — every required field must be filled before
  // the "Register player" button unlocks: photo, name, phone, DOB,
  // gender, city, state, country, and a captured MFS110 fingerprint.
  // ---------------------------------------------------------------
  const phoneDigits = form.phone.replace(/\D/g, "");
  const isFormComplete =
    !!photoB64 &&
    !!form.full_name.trim() &&
    phoneDigits.length >= 7 &&
    !!form.dob &&
    !!form.gender &&
    !!form.city.trim() &&
    !!form.state.trim() &&
    !!form.country.trim() &&
    !!fingerprint?.template;

  const submit = async () => {
    if (!isFormComplete) {
      return toast.error(
        "Please fill every field (photo, name, phone, DOB, gender, city, state, country) and capture the fingerprint.",
      );
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/portal/players", {
        ...form,
        photo_b64: photoB64 || null,
        dob: form.dob || null,
        gender: form.gender || null,
        fingerprint_template: fingerprint.template,
      });
      toast.success(`Player registered. Code: ${data.unique_code}`);
      onCreated(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create player.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="add-player-form">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="text-emerald-300 hover:text-emerald-200 flex items-center gap-1 text-sm" data-testid="add-player-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h3 className="text-lg font-semibold text-emerald-100">Register a new player</h3>
      </div>

      <div
        className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200 flex items-start gap-2"
        data-testid="required-fields-hint"
      >
        <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <span className="font-semibold">All fields are required.</span>{" "}
          Photo, full name, phone, date of birth, gender, city, state, country
          and a <b>Mantra MFS110</b> fingerprint scan must all be captured —
          the <b>Register player</b> button unlocks only when every field is
          complete.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
        {/* photo */}
        <div className="space-y-2">
          <div className="aspect-square w-full rounded-xl overflow-hidden bg-zinc-800 border border-zinc-700 grid place-items-center">
            {streamOn ? (
              <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
            ) : photoB64 ? (
              <img src={photoB64} alt="player" className="w-full h-full object-cover" />
            ) : (
              <div className="text-gray-500 text-xs uppercase tracking-widest text-center px-2">
                No photo
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {!streamOn ? (
              <button onClick={startCamera} type="button" className="flex-1 px-2 py-1.5 text-xs rounded-md bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30 flex items-center justify-center gap-1" data-testid="open-camera">
                <Camera className="h-3.5 w-3.5" /> Camera
              </button>
            ) : (
              <>
                <button onClick={capture} type="button" className="flex-1 px-2 py-1.5 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700" data-testid="capture-photo">
                  Capture
                </button>
                <button onClick={stopCamera} type="button" className="px-2 py-1.5 text-xs rounded-md bg-zinc-700 text-gray-200">
                  Cancel
                </button>
              </>
            )}
          </div>
          <label className="block">
            <span className="sr-only">Upload</span>
            <input type="file" accept="image/*" onChange={onFile} className="text-xs text-gray-400 file:mr-2 file:rounded file:border-0 file:bg-zinc-800 file:text-gray-200 file:px-2 file:py-1 file:text-xs" data-testid="photo-upload" />
          </label>
        </div>

        {/* fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Full name" required>
            <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} data-testid="player-name" />
          </Field>
          <Field label="Phone" required hint="Used as cross-club unique key">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="player-phone" />
          </Field>
          <Field label="Date of birth" required>
            <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className={inputCls} data-testid="player-dob" />
          </Field>
          <Field label="Gender" required>
            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={inputCls} data-testid="player-gender">
              <option value="">—</option>
              {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="City" required>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="player-city" />
          </Field>
          <Field label="State" required>
            <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} data-testid="player-state" />
          </Field>
          <Field label="Country" required>
            <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inputCls} data-testid="player-country" />
          </Field>
          <Field
            label="Fingerprint (Mantra MFS110)"
            required
            hint={
              fingerprint
                ? `Captured · quality ${fingerprint.quality || "n/a"}`
                : "Plug in the MFS110 and press Scan"
            }
          >
            {fingerprint ? (
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-200"
                data-testid="fingerprint-captured"
              >
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-xs flex-1 truncate">
                  ISO template ready
                </span>
                <button
                  type="button"
                  onClick={clearFingerprint}
                  className="text-[10px] uppercase tracking-widest text-emerald-300 hover:text-emerald-100"
                  data-testid="fingerprint-rescan"
                >
                  Rescan
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={scanFingerprint}
                disabled={fpBusy}
                className="px-3 py-2 text-xs rounded-md bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30 disabled:opacity-50 flex items-center gap-2"
                data-testid="fingerprint-scan"
              >
                {fpBusy ? <Spinner /> : <FingerprintIcon className="h-3.5 w-3.5" />}
                {fpBusy ? "Place finger…" : "Scan fingerprint"}
              </button>
            )}
          </Field>
        </div>
      </div>

      {existingMatch && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200 flex items-start gap-2" data-testid="duplicate-phone-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">A player with this phone already exists.</div>
            <div className="text-amber-100/80 text-xs mt-0.5">
              {existingMatch.full_name} ({existingMatch.unique_code}) — registered at {existingMatch.registered_by_cafe_name || "another club"}.
              Use the search bar to open their profile instead of creating a duplicate.
            </div>
          </div>
        </div>
      )}
      {!existingMatch && phoneCheckMsg && (
        <div className="text-xs text-emerald-300">{phoneCheckMsg}</div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
        <button onClick={onCancel} className="px-4 py-2 text-sm rounded-md bg-zinc-800 text-gray-300 hover:bg-zinc-700" data-testid="add-player-cancel">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={submitting || !isFormComplete || !!existingMatch}
          title={
            !isFormComplete
              ? "Fill every Aadhaar-proof field to unlock"
              : existingMatch
                ? "This phone is already registered"
                : ""
          }
          className="px-4 py-2 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          data-testid="add-player-submit"
        >
          {submitting && <Spinner />} Register player
        </button>
      </div>
    </div>
  );
}

// Tiny inline Fingerprint glyph because lucide one is already imported above.
function Fingerprint(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" {...props}>
      <path d="M16 14a4 4 0 0 0-8 0c0 1.5.4 2.9 1 4.1" />
      <path d="M12 6c-2.2 0-4 1.8-4 4" />
      <path d="M20 14c0-4.4-3.6-8-8-8a7.9 7.9 0 0 0-5.6 2.3" />
      <path d="M12 14v4" />
    </svg>
  );
}

const inputCls = "w-full px-3 py-2 text-sm rounded-md bg-zinc-900 border border-zinc-700 focus:border-emerald-500 focus:outline-none text-gray-100 placeholder:text-gray-500";

function Field({ label, required, hint, children }) {
  return (
    <label className="block space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-gray-400">
          {label}{required && <span className="text-rose-400"> *</span>}
        </span>
        {hint && <span className="text-[10px] text-gray-500">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

/* ------------------------------------------------------------------ */
/*                          Player Detail View                         */
/* ------------------------------------------------------------------ */

function PlayerDetail({ playerId, onBack, onChanged }) {
  const { user } = useAuth();
  const isPlatform = user?.role === "platform";
  const isOwnerOrAbove = user?.role === "owner" || isPlatform;
  // Owners see their cafe's members without any masking of the phone; the
  // request was to strip the *protected* phone display for owner sessions so
  // player numbers stay off screen entirely.
  const hidePhone = user?.role === "owner";

  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/players/${playerId}`);
      setPlayer(data);
    } catch (e) {
      toast.error("Could not load player.");
    } finally { setLoading(false); }
  }, [playerId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live refresh — refetch this player whenever a payment/blacklist/damage
  // change ripples in from elsewhere (UnpaidSessionsPanel, etc.).
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("portal:refresh", handler);
    return () => window.removeEventListener("portal:refresh", handler);
  }, [refresh]);

  const fireRefresh = () => {
    try { window.dispatchEvent(new CustomEvent("portal:refresh")); } catch { /* ignore */ }
  };

  const copyCode = async () => {
    try {
      const value = player.barcode || player.unique_code;
      await navigator.clipboard.writeText(value);
      toast.success("Member ID copied to clipboard.");
    } catch { toast.error("Copy failed."); }
  };

  const onBlacklist = async () => {
    const reason = window.prompt("Reason for blacklist?");
    if (!reason || !reason.trim()) return;
    const amtStr = window.prompt("Outstanding amount due (₹)?", "0");
    if (amtStr === null) return;
    const amount_due = Number(amtStr) || 0;
    try {
      await api.post(`/portal/players/${player.id}/blacklist`, { reason: reason.trim(), amount_due });
      toast.success("Player blacklisted.");
      await refresh();
      fireRefresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to blacklist.");
    }
  };

  const onRelease = async (bid) => {
    // Platform-admin only. Backend enforces it; we mirror the flow here.
    const bl = (player.blacklists || []).find((b) => b.id === bid);
    const paidAns = window.confirm(
      `Release ${player.full_name} from the blacklist?\n\nDid the player pay the outstanding amount?\n\nOK = Yes (I'll enter the amount)\nCancel = No (skip payment, force release)`,
    );
    let amount_paid = 0;
    if (paidAns) {
      const suggested = String(bl?.amount_due || 0);
      const amtStr = window.prompt(
        `How much did the player pay? (₹)\n\nBlacklist due on record: ₹${bl?.amount_due || 0}`,
        suggested,
      );
      if (amtStr === null) return; // user hit Cancel on the amount prompt
      amount_paid = Number(amtStr);
      if (!Number.isFinite(amount_paid) || amount_paid < 0) {
        toast.error("Enter a valid non-negative amount.");
        return;
      }
    } else if (!window.confirm(
      "Release WITHOUT recording a payment? The blacklist will be cleared regardless.",
    )) {
      return;
    }
    try {
      await api.post(
        `/portal/players/${player.id}/blacklist/${bid}/release`,
        { amount_paid },
      );
      toast.success(
        amount_paid > 0
          ? `Blacklist released. ₹${amount_paid} recorded as paid.`
          : "Blacklist released.",
      );
      await refresh();
      fireRefresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to release.");
    }
  };

  const onDownloadIdCard = async () => {
    try {
      const res = await api.get(
        `/portal/players/${player.id}/id-card.png`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cue-lounge-id-${player.barcode || player.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("ID card downloaded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download ID card.");
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Permanently delete ${player.full_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/portal/players/${player.id}`);
      toast.success("Player deleted.");
      fireRefresh();
      onChanged?.();
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete.");
    }
  };

  if (loading || !player) {
    return <div className="p-10 grid place-items-center text-gray-500"><Spinner /></div>;
  }

  const openBl = (player.blacklists || []).filter((b) => !b.released_at);
  const dues = openBl.reduce((s, b) => s + (Number(b.amount_due) || 0), 0);

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-5 pb-3 border-b border-zinc-800 flex flex-col gap-4">
        <button onClick={onBack} className="self-start text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 text-sm" data-testid="detail-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-start gap-4">
          {player.photo_b64 ? (
            <img src={player.photo_b64} alt={player.full_name} className="h-20 w-20 rounded-xl object-cover border border-zinc-700 shrink-0" />
          ) : (
            <div className="h-20 w-20 rounded-xl bg-zinc-800 grid place-items-center text-gray-500 text-2xl border border-zinc-700 shrink-0">
              {player.full_name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="flex-1 min-w-0 space-y-1">
            <h3 className="text-xl font-semibold text-emerald-100" data-testid="detail-name">{player.full_name}</h3>
            <button onClick={copyCode} className="inline-flex items-center gap-2 text-sm font-mono text-emerald-300 hover:text-emerald-200" data-testid="copy-code">
              {player.barcode || player.unique_code} <Copy className="h-3.5 w-3.5" />
            </button>
            <div className="flex flex-col gap-1 text-xs text-gray-400">
              {!hidePhone && (
                <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{player.phone}</span>
              )}
              {player.dob && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{player.dob}</span>}
              {(player.city || player.state) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[player.city, player.state, player.country].filter(Boolean).join(", ")}</span>}
            </div>
            {openBl.length > 0 && (
              <div className="mt-1 inline-flex items-center gap-2 px-2 py-1 rounded-md bg-rose-500/15 border border-rose-400/30 text-rose-200 text-xs" data-testid="detail-blacklisted">
                <ShieldAlert className="h-3.5 w-3.5" /> Blacklisted — outstanding {formatINR(dues)}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 self-start">
          <img
            alt="Barcode"
            src={`${API_BASE}/portal/players/${player.id}/barcode.png`}
            className="h-24 bg-white rounded-md p-1"
            data-testid="player-barcode"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-widest text-gray-500">Member barcode</span>
            <span className="font-mono text-sm text-emerald-200" data-testid="player-barcode-number">
              {player.barcode || "—"}
            </span>
          </div>
        </div>
      </div>

      {/* tabs */}
      <div className="px-6 pt-3 flex gap-1 border-b border-zinc-800">
        {[
          { k: "profile",  label: "Profile" },
          { k: "history",  label: `History (${(player.history || []).length})` },
          { k: "damages",  label: `Damages (${(player.damages || []).length})` },
          { k: "blacklist", label: `Blacklist (${(player.blacklists || []).length})` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 transition ${
              tab === t.k
                ? "border-emerald-400 text-emerald-200"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* tab body */}
      <div className="flex-1 overflow-y-auto p-6">
        {tab === "profile" && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <InfoRow label="Date of birth"     value={player.dob || "—"} />
            <InfoRow label="Gender"            value={player.gender || "—"} />
            <InfoRow label="City / State"      value={[player.city, player.state].filter(Boolean).join(", ") || "—"} />
            <InfoRow label="Country"           value={player.country || "—"} />
            <InfoRow label="Registered at"     value={player.registered_by_cafe_name || "—"} />
            <InfoRow label="Registered on"     value={formatDateTime(player.registered_at)} />
            <InfoRow label="Sessions played"   value={(player.history || []).length} />
            <InfoRow label="Outstanding dues"  value={formatINR(openBl.reduce((s, b) => s + (Number(b.amount_due) || 0), 0))} />
            <InfoRow label="Fingerprint"       value={player.fingerprint_template ? "On file" : "Not captured"} />
          </div>
        )}

        {tab === "history" && (
          <HistoryTab rows={player.history || []} />
        )}

        {tab === "damages" && (
          <DamagesTab rows={player.damages || []} />
        )}

        {tab === "blacklist" && (
          <BlacklistTab
            rows={player.blacklists || []}
            canAct={isPlatform}
            myCafeId={user?.cafe_id}
            onRelease={onRelease}
            isPlatform={isPlatform}
          />
        )}
      </div>

      {/* footer actions */}
      <div className="px-6 py-3 border-t border-zinc-800 flex justify-between gap-2">
        <div className="flex gap-2">
          {isOwnerOrAbove && (
            <button onClick={onBlacklist} className="px-3 py-1.5 text-xs rounded-md bg-rose-600/20 border border-rose-500/40 text-rose-200 hover:bg-rose-600/30 inline-flex items-center gap-2" data-testid="action-blacklist">
              <ShieldAlert className="h-3.5 w-3.5" /> Blacklist
            </button>
          )}
        </div>
        <div className="flex gap-2">
          {isPlatform && (
            <button
              onClick={onDownloadIdCard}
              className="px-3 py-1.5 text-xs rounded-md bg-emerald-600/25 border border-emerald-500/40 text-emerald-100 hover:bg-emerald-600/40 inline-flex items-center gap-2"
              data-testid="action-download-id"
            >
              <QrCode className="h-3.5 w-3.5" /> Download ID card
            </button>
          )}
          {isPlatform && (
            <button onClick={onDelete} className="px-3 py-1.5 text-xs rounded-md bg-rose-700/30 border border-rose-500/40 text-rose-200 hover:bg-rose-700/50 inline-flex items-center gap-2" data-testid="action-delete">
              <Trash2 className="h-3.5 w-3.5" /> Delete (platform only)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-gray-500">{label}</span>
      <span className="text-gray-100 text-sm">{value}</span>
    </div>
  );
}

function HistoryTab({ rows }) {
  if (!rows.length) return <div className="text-center text-gray-500 py-12 text-sm">No sessions logged yet.</div>;
  return (
    <table className="w-full text-sm" data-testid="history-table">
      <thead>
        <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-zinc-800">
          <th className="text-left py-2">Date</th>
          <th className="text-left py-2">Club</th>
          <th className="text-left py-2">Table</th>
          <th className="text-right py-2">Duration</th>
          <th className="text-right py-2">Amount</th>
          <th className="text-left py-2 pl-3">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice().reverse().map((h) => (
          <tr key={h.id} className="border-b border-zinc-800/60">
            <td className="py-2 text-gray-300">{formatDateTime(h.ended_at || h.started_at)}</td>
            <td className="py-2 text-gray-300">{h.cafe_name || "—"}</td>
            <td className="py-2 text-gray-400">{h.table_name || "—"}</td>
            <td className="py-2 text-right text-gray-400">{h.duration_minutes ?? "—"} min</td>
            <td className="py-2 text-right text-gray-200">{formatINR(h.amount)}</td>
            <td className="py-2 pl-3">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                h.payment_status === "paid"
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30"
                  : "bg-amber-500/15 text-amber-300 border border-amber-400/30"
              }`}>
                {h.payment_status || "—"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DamagesTab({ rows }) {
  if (!rows.length) return <div className="text-center text-gray-500 py-12 text-sm">No damages recorded.</div>;
  return (
    <ul className="space-y-2" data-testid="damages-list">
      {rows.slice().reverse().map((d) => (
        <li key={d.id} className="flex items-start justify-between gap-3 p-3 rounded-md border border-zinc-800 bg-zinc-900/50">
          <div>
            <div className="text-sm text-gray-100">{d.description}</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
              {d.cafe_name || "—"} · {formatDateTime(d.recorded_at)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-100">{formatINR(d.amount)}</div>
            <div className={`text-[10px] mt-1 ${d.paid ? "text-emerald-300" : "text-amber-300"}`}>
              {d.paid ? "Paid" : "Unpaid"}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function BlacklistTab({ rows, canAct, myCafeId, onRelease, isPlatform }) {
  if (!rows.length) return <div className="text-center text-gray-500 py-12 text-sm">No blacklist history.</div>;
  return (
    <ul className="space-y-2" data-testid="blacklist-list">
      {rows.slice().reverse().map((b) => {
        const active = !b.released_at;
        const canRelease = canAct && active && (isPlatform || b.cafe_id === myCafeId);
        return (
          <li key={b.id} className={`p-3 rounded-md border ${active ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  {active ? <ShieldAlert className="h-4 w-4 text-rose-300" /> : <ShieldCheck className="h-4 w-4 text-emerald-300" />}
                  <span className="text-sm text-gray-100">{b.reason}</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                  {b.cafe_name || "—"} · {formatDateTime(b.blacklisted_at)}
                  {b.released_at && <> · released {formatDateTime(b.released_at)}</>}
                  {!!b.amount_paid && <> · paid <span className="text-emerald-300">{formatINR(b.amount_paid)}</span></>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-100">{formatINR(b.amount_due)}</div>
                {canRelease && (
                  <button onClick={() => onRelease(b.id)} className="mt-2 text-[10px] uppercase tracking-widest text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1" data-testid={`release-${b.id}`}>
                    <RotateCcw className="h-3 w-3" /> Mark paid & release
                  </button>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------------ */
/*                          List + Search View                         */
/* ------------------------------------------------------------------ */

function ListView({ onOpenAdd, onOpenDetail }) {
  const { user } = useAuth();
  const hidePhone = user?.role === "owner";
  // Admin (Club staff) can ONLY search by numeric barcode / NFC / scanner.
  // Owner & Platform keep the fuller text search so they can also look up by
  // name or the legacy CGP code.
  const numericOnly = user?.role === "admin";
  const [query, setQuery] = useState("");
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crossClubHit, setCrossClubHit] = useState(null); // result of lookup outside my cafe
  const [nfcBusy, setNfcBusy] = useState(false);
  const nfcAbortRef = useRef(null);
  const searchInputRef = useRef(null);

  const fetchList = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/portal/players", { params: { query: q || undefined, limit: 500 } });
      setPlayers(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(""); }, [fetchList]);

  // Live refresh — re-fetch the list whenever something somewhere flips a
  // payment / blacklist / damage state. Cheap because the list is small.
  useEffect(() => {
    const handler = () => fetchList(query);
    window.addEventListener("portal:refresh", handler);
    return () => window.removeEventListener("portal:refresh", handler);
  }, [fetchList, query]);

  // Cross-club lookup on every keystroke (debounced) — if the local list
  // doesn't contain it, surface it via the lookup endpoint.
  useEffect(() => {
    setCrossClubHit(null);
    const q = query.trim();
    if (q.length < 4) return;
    const t = setTimeout(async () => {
      try {
        const params = {};
        const digits = q.replace(/\D/g, "");
        // 8–14 digits looks like a barcode → do a direct barcode lookup.
        if (digits && digits.length >= 8 && digits.length <= 14) params.barcode = digits;
        else if (/^CGP-/i.test(q)) params.code = q;
        else if (/^\d{7,}$/.test(digits)) params.phone = q;
        else return;
        const { data } = await api.get("/portal/players/lookup", { params });
        // Only surface if not already in the current list
        if (!players.find((p) => p.id === data.id)) setCrossClubHit(data);
      } catch { /* 404 → ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [query, players]);

  // Barcode-reader / NFC: when a rapidly-typed 12-digit sequence ends with
  // Enter (or the input reaches 12 digits), jump straight into that player's
  // detail view.
  const onSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const digits = query.replace(/\D/g, "");
    if (!digits) return;
    const local = players.find((p) => p.barcode === digits);
    if (local) { onOpenDetail(local.id); return; }
    if (crossClubHit && crossClubHit.barcode === digits) {
      onOpenDetail(crossClubHit.id);
    }
  };

  // Web NFC scan (Android Chrome / Edge on HTTPS). We treat any text record
  // on the NFC tag as the 12-digit barcode payload — matches how the
  // platform-issued ID card is programmed onto a blank NFC tag.
  const scanNfc = async () => {
    if (typeof window === "undefined" || !("NDEFReader" in window)) {
      toast.error(
        "Web NFC isn't supported on this device/browser. Use Chrome on Android, or scan the barcode instead.",
      );
      return;
    }
    setNfcBusy(true);
    try {
      // eslint-disable-next-line no-undef
      const reader = new NDEFReader();
      const controller = new AbortController();
      nfcAbortRef.current = controller;
      await reader.scan({ signal: controller.signal });
      toast.info("Tap the NFC card to the phone…");
      reader.onreading = (event) => {
        for (const record of event.message.records) {
          try {
            const decoder = new TextDecoder(record.encoding || "utf-8");
            const text = decoder.decode(record.data);
            const digits = String(text).replace(/\D/g, "");
            if (digits) {
              setQuery(digits);
              controller.abort();
              setNfcBusy(false);
              // Auto-open the matching player if we already have them.
              const local = players.find((p) => p.barcode === digits);
              if (local) onOpenDetail(local.id);
              return;
            }
          } catch { /* try next record */ }
        }
      };
    } catch (err) {
      setNfcBusy(false);
      toast.error(err?.message || "Failed to start NFC scan.");
    }
  };

  useEffect(() => () => nfcAbortRef.current?.abort(), []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) =>
      (p.full_name || "").toLowerCase().includes(q) ||
      (p.phone || "").includes(q) ||
      (p.unique_code || "").toLowerCase().includes(q) ||
      (p.barcode || "").includes(q.replace(/\D/g, "")),
    );
  }, [players, query]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-5 pb-3 flex items-center gap-3 border-b border-zinc-800">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            ref={searchInputRef}
            value={query}
            onChange={(e) => {
              const v = e.target.value;
              setQuery(numericOnly ? v.replace(/\D/g, "").slice(0, 14) : v);
            }}
            onKeyDown={onSearchKeyDown}
            inputMode={numericOnly ? "numeric" : "text"}
            placeholder={
              numericOnly
                ? "Scan barcode, tap NFC or type member number…"
                : "Scan barcode, tap NFC, or search by name / phone…"
            }
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md bg-zinc-900 border border-zinc-700 focus:border-emerald-500 focus:outline-none text-gray-100"
            data-testid="player-search"
          />
        </div>
        <button
          type="button"
          onClick={scanNfc}
          disabled={nfcBusy}
          title="Tap an NFC member card"
          className="px-3 py-2 text-sm rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/25 disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="scan-nfc-btn"
        >
          {nfcBusy ? <Spinner /> : <Hash className="h-4 w-4" />}
          NFC
        </button>
        <button onClick={onOpenAdd} className="px-3 py-2 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-2" data-testid="open-add-player">
          <UserPlus className="h-4 w-4" /> Add Player
        </button>
      </div>

      {/* cross-club hit banner */}
      {crossClubHit && (
        <button
          onClick={() => onOpenDetail(crossClubHit.id)}
          className="mx-6 mt-4 text-left rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 hover:bg-amber-400/15"
          data-testid="cross-club-result"
        >
          <div className="text-amber-200 text-xs uppercase tracking-widest mb-0.5">Match from another club</div>
          <div className="text-sm text-amber-50 flex items-center gap-2">
            {crossClubHit.full_name} <span className="font-mono text-xs text-amber-200">{crossClubHit.barcode || crossClubHit.unique_code}</span>
            {crossClubHit.blacklists?.some((b) => !b.released_at) && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 text-[10px]">
                <ShieldAlert className="h-3 w-3" /> BLACKLISTED
              </span>
            )}
          </div>
        </button>
      )}

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="grid place-items-center py-20 text-gray-500"><Spinner /></div>
        ) : visible.length === 0 ? (
          <div className="text-center text-gray-500 py-12 text-sm" data-testid="no-players">
            No players yet. Click <span className="text-emerald-300">Add Player</span> to register the first one.
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="players-list">
            {visible.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => onOpenDetail(p.id)}
                  data-testid={`player-row-${p.id}`}
                  className="w-full text-left p-3 rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-emerald-500/40 transition flex items-center gap-3"
                >
                  <div className="h-12 w-12 rounded-md bg-zinc-800 grid place-items-center text-gray-400 shrink-0">
                    {p.full_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-100 truncate flex items-center gap-2">
                      {p.full_name}
                      {p.is_blacklisted && (
                        <ShieldAlert className="h-3.5 w-3.5 text-rose-400 shrink-0" title="Blacklisted" />
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono truncate">
                      {p.barcode || p.unique_code}{!hidePhone && p.phone ? ` · ${p.phone}` : ""}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5 inline-flex items-center gap-2">
                      <History className="h-3 w-3" /> {p.history_count} session{p.history_count === 1 ? "" : "s"}
                      {p.outstanding_amount > 0 && (
                        <span className="text-rose-300">· dues {formatINR(p.outstanding_amount)}</span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                            Main Modal                               */
/* ------------------------------------------------------------------ */

export default function PlayersPortalModal({ open, onClose }) {
  const [view, setView] = useState("list"); // list | add | detail
  const [activeId, setActiveId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (open) { setView("list"); setActiveId(null); }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="players-portal-modal">
      <div className="w-full max-w-5xl h-[90vh] bg-[#0a0f0d] border border-emerald-500/20 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Hash className="h-4 w-4 text-emerald-300" />
            <h2 className="text-sm uppercase tracking-widest text-emerald-100 font-semibold">Players Portal</h2>
            <span className="text-[10px] uppercase tracking-widest text-gray-500">Cross-club registry</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="portal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden" key={refreshKey}>
          {view === "list" && (
            <ListView
              onOpenAdd={() => setView("add")}
              onOpenDetail={(id) => { setActiveId(id); setView("detail"); }}
            />
          )}
          {view === "add" && (
            <AddPlayerForm
              onCancel={() => setView("list")}
              onCreated={(id) => { setActiveId(id); setView("detail"); }}
            />
          )}
          {view === "detail" && activeId && (
            <PlayerDetail
              playerId={activeId}
              onBack={() => { setView("list"); setRefreshKey((k) => k + 1); }}
              onChanged={() => setRefreshKey((k) => k + 1)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
